import { defineNuxtPlugin } from '#app';

// Usage: v-sticky="{ disabled: !isLargeScreen }"

export default defineNuxtPlugin((nuxtApp) => {
    const stickyDirective = {
        mounted(el, binding) {
            const createPlaceholder = () => {
                const placeholder = document.createElement('div');
                placeholder.style.width = `${el.offsetWidth}px`;
                placeholder.style.height = `${el.offsetHeight}px`;
                placeholder.style.display = 'none';
                el.parentNode.insertBefore(placeholder, el);
                return placeholder;
            };

            const placeholder = createPlaceholder();

            const originalStyles = {
                position: el.style.position,
                top: el.style.top,
                left: el.style.left,
                width: el.style.width,
                zIndex: el.style.zIndex,
            };

            let triggerPoint = 0;
            let originalLeft = 0;

            const recalc = () => {
                // reset before calculating
                el.style.position = originalStyles.position;
                el.style.top = originalStyles.top;
                el.style.left = originalStyles.left;
                el.style.width = originalStyles.width;

                placeholder.style.display = 'none';
                el.classList.remove('v-sticky--active');

                const rect = el.getBoundingClientRect();
                triggerPoint = rect.top + window.scrollY;
                originalLeft = rect.left + window.scrollX;

                placeholder.style.width = `${el.offsetWidth}px`;
                placeholder.style.height = `${el.offsetHeight}px`;
            };

            const activateSticky = () => {
                if (!el.classList.contains('v-sticky--active')) {
                    placeholder.style.display = '';
                    el.classList.add('v-sticky--active');
                    el.style.position = 'fixed';
                    el.style.top = '0';
                    el.style.left = `${originalLeft}px`;
                    el.style.width = `${placeholder.offsetWidth}px`;
                    el.style.zIndex = '99';
                }
            };

            const deactivateSticky = () => {
                if (el.classList.contains('v-sticky--active')) {
                    placeholder.style.display = 'none';
                    el.classList.remove('v-sticky--active');
                    el.style.position = originalStyles.position;
                    el.style.top = originalStyles.top;
                    el.style.left = originalStyles.left;
                    el.style.width = originalStyles.width;
                    el.style.zIndex = originalStyles.zIndex;
                }
            };

            const onScroll = () => {
                const disabled = binding.value?.disabled === true;

                if (disabled) {
                    deactivateSticky();
                    return;
                }

                if (window.scrollY >= triggerPoint) {
                    activateSticky();
                } else {
                    deactivateSticky();
                }
            };

            const onResize = () => {
                recalc();
                onScroll();
            };

            el._onStickyScroll_ = onScroll;
            el._onStickyResize_ = onResize;
            el._stickyPlaceholder_ = placeholder;
            el._stickyOriginalStyles_ = originalStyles;

            recalc();

            window.addEventListener('scroll', onScroll);
            window.addEventListener('resize', onResize);
        },

        updated(el, binding) {
            const disabled = binding.value?.disabled === true;

            if (disabled) {
                const placeholder = el._stickyPlaceholder_;
                const originalStyles = el._stickyOriginalStyles_;
                if (placeholder) placeholder.style.display = 'none';
                el.classList.remove('v-sticky--active');
                el.style.position = originalStyles.position;
                el.style.top = originalStyles.top;
                el.style.left = originalStyles.left;
                el.style.width = originalStyles.width;
                el.style.zIndex = originalStyles.zIndex;
            } else {
                if (el._onStickyResize_) el._onStickyResize_();
            }
        },

        unmounted(el) {
            if (el._onStickyScroll_) window.removeEventListener('scroll', el._onStickyScroll_);
            if (el._onStickyResize_) window.removeEventListener('resize', el._onStickyResize_);
            delete el._onStickyScroll_;
            delete el._onStickyResize_;
            delete el._stickyPlaceholder_;
            delete el._stickyOriginalStyles_;
        },
    };

    nuxtApp.vueApp.directive('sticky', stickyDirective);
});
